import { createContext, useContext, useState, type ReactNode } from 'react'
import { FicheVoiceSection } from '../components/voice/FicheVoiceSection.js'

// ── Types ──

export interface MandatVoiceState {
  mandatId: string | null
  mandatLabel?: string
  bienId?: string
  autoPlay?: boolean
}

export interface MandatVoiceContextValue extends MandatVoiceState {
  setMandat: (state: Omit<MandatVoiceState, 'autoPlay'> & { autoPlay?: boolean } | null) => void
  clearMandat: () => void
  requestTTS: () => void
  requestSTT: () => void
}

// ── Context ──

const MandatVoiceContext = createContext<MandatVoiceContextValue | null>(null)

// ── Provider ──

export function MandatVoiceProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<MandatVoiceState>({ mandatId: null })
  const [ttsRequested, setTtsRequested] = useState(false)
  const [sttRequested, setSttRequested] = useState(false)

  function setMandat(next: Omit<MandatVoiceState, 'autoPlay'> & { autoPlay?: boolean } | null) {
    if (!next) {
      setState({ mandatId: null })
      setTtsRequested(false)
      setSttRequested(false)
    } else {
      setState(next)
      // Si autoPlay demandé, déclencher TTS automatiquement
      if (next.autoPlay) setTtsRequested(true)
    }
  }

  function clearMandat() {
    setState({ mandatId: null })
    setTtsRequested(false)
    setSttRequested(false)
  }

  function requestTTS() {
    if (state.mandatId) setTtsRequested(true)
  }

  function requestSTT() {
    if (state.mandatId) setSttRequested(true)
  }

  function handleVoiceClose() {
    setTtsRequested(false)
    setSttRequested(false)
  }

  const value: MandatVoiceContextValue = {
    ...state,
    setMandat,
    clearMandat,
    requestTTS,
    requestSTT,
  }

  return (
    <MandatVoiceContext.Provider value={value}>
      {children}
      {state.mandatId && (ttsRequested || sttRequested) && (
        <FicheVoiceSection
          mandatId={state.mandatId}
          mandatLabel={state.mandatLabel}
          bienId={state.bienId}
          autoPlay={ttsRequested && !sttRequested}
          startSTT={sttRequested}
          onClose={handleVoiceClose}
        />
      )}
    </MandatVoiceContext.Provider>
  )
}

// ── Hook ──

export function useMandatVoice(): MandatVoiceContextValue {
  const ctx = useContext(MandatVoiceContext)
  if (!ctx) {
    throw new Error('useMandatVoice doit être utilisé dans MandatVoiceProvider')
  }
  return ctx
}
